import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      if (u.must_change_password) navigate("/admin/change-password");
      else navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex bg-zinc-900 text-white overflow-hidden grain-overlay">
        <img
          src="https://images.unsplash.com/photo-1582139329536-e7284fece509?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Vault"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20}/>
            <span className="tech-label text-zinc-300">IIBF · BCBF · Admin</span>
          </div>
          <div>
            <h2 className="font-display text-5xl font-black tracking-tighter leading-none">Secure administrative access.</h2>
            <p className="mt-4 text-zinc-300 max-w-md leading-relaxed">Manage BCBF applications, review documents, and update statuses. All sessions are token-authenticated and audit-logged.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <form onSubmit={handle} className="w-full max-w-sm" data-testid="admin-login-form">
          <Link to="/" className="tech-label hover:text-zinc-900">← Back to portal</Link>
          <h1 className="mt-6 font-display text-4xl font-black tracking-tighter">Sign In</h1>
          <p className="mt-2 text-sm text-zinc-500">Administrator credentials required</p>
          <div className="mt-8 space-y-5">
            <div>
              <Label className="tech-label">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="h-12 bg-zinc-50 mt-2" data-testid="admin-email-input"/>
            </div>
            <div>
              <Label className="tech-label">Password</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="h-12 bg-zinc-50 mt-2" data-testid="admin-password-input"/>
            </div>
            <Button type="submit" disabled={busy} data-testid="admin-login-btn" className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold">
              <Lock size={16} className="mr-2"/>{busy ? "Signing in…" : "Sign In"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
